import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { formatPriceTtc } from '../utils/commerce';
import './ClickCollectRestaurantMenu.css';

const ORNAMENT = (
  <svg viewBox="0 0 70 70" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden>
    <path d="M54 8C35 8 38 24 48 24S60 10 48 10M43 29C19 19 10 35 20 43S37 42 32 34M21 15C5 19 8 35 18 30S18 18 12 22M52 43C35 39 37 58 49 55S58 46 50 47M8 53C15 46 24 48 22 59S9 65 8 58" />
    <path d="M34 8Q28 20 35 25M10 41Q23 44 25 52" />
  </svg>
);

function splitGroupedColumns(grouped) {
  if (!grouped.length) return [[], []];
  const mid = Math.ceil(grouped.length / 2);
  return [grouped.slice(0, mid), grouped.slice(mid)];
}

function MenuSection({ category, items, cart, onAddItem }) {
  return (
    <section className="menu-section">
      <h2>{category}</h2>
      {items.map((item) => {
        const max = Number(item.quantityAvailable) || 0;
        const qty = Number(cart[item.id]) || 0;
        const disabled = max <= 0 || qty >= max;
        return (
          <button
            key={item.id}
            type="button"
            className={`menu-dish${qty > 0 ? ' is-selected' : ''}`}
            disabled={disabled}
            onClick={() => onAddItem(item.id, max)}
            title={disabled && max <= 0 ? 'Indisponible' : 'Ajouter au panier'}
          >
            <div className="dishline">
              <span>{item.title}</span>
              <span className="dots" />
              <span>{formatPriceTtc(item.priceTtc)}</span>
              {qty > 0 ? <span className="dish-qty">×{qty}</span> : null}
            </div>
            {item.description ? (
              <div className="description">{item.description}</div>
            ) : (
              <div className="description" aria-hidden>
                {' '}
              </div>
            )}
          </button>
        );
      })}
    </section>
  );
}

export default function ClickCollectRestaurantMenu({
  title,
  subtitle = 'vous propose',
  footerLines = [],
  grouped = [],
  cart = {},
  onAddItem,
  menuTheme = {},
}) {
  const showOrnaments = menuTheme.showOrnaments !== false;
  const showFrame = menuTheme.showFrame !== false;
  const ink = menuTheme.inkColor || '#432728';
  const sheetClass = [
    'click-collect-menu-sheet',
    !showOrnaments ? 'no-ornaments' : '',
    !showFrame ? 'no-frame' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const [leftCol, rightCol] = useMemo(() => splitGroupedColumns(grouped), [grouped]);

  return (
    <div className="click-collect-menu-root">
      <Helmet>
        <link
          href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Cormorant+Garamond:wght@400;500;600&family=Montserrat:wght@400;500;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <div className="click-collect-menu-viewport">
        <div className="click-collect-menu-scaler">
          <main className={sheetClass} style={{ '--menu-ink': ink }}>
            <div className="frame-top" />
            <div className="frame-bottom" />
            <div className="frame-left" />
            <div className="frame-right" />
            <div className="ornament tl">{ORNAMENT}</div>
            <div className="ornament tr">{ORNAMENT}</div>
            <div className="ornament bl">{ORNAMENT}</div>
            <div className="ornament br">{ORNAMENT}</div>

            <header>
              <h1>{title}</h1>
              {subtitle ? <p>{subtitle}</p> : null}
            </header>

            <div className="menu-columns">
              <div>
                {leftCol.map(([category, catItems]) => (
                  <MenuSection
                    key={category}
                    category={category}
                    items={catItems}
                    cart={cart}
                    onAddItem={onAddItem}
                  />
                ))}
              </div>
              <div>
                {rightCol.map(([category, catItems]) => (
                  <MenuSection
                    key={category}
                    category={category}
                    items={catItems}
                    cart={cart}
                    onAddItem={onAddItem}
                  />
                ))}
              </div>
            </div>

            {footerLines.filter(Boolean).length ? (
              <footer className="menu-footer">
                {footerLines.filter(Boolean).map((line, i) => (
                  <div key={`f-${i}`}>{line}</div>
                ))}
              </footer>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
}
